import { styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

import { Divider } from '../../atoms/Divider';
import { Stack } from '../../atoms/Stack';
import { Card } from '../Card';

type Props = {
  title: string;
  href: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tags?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  'data-testid'?: string;
};

const HOVER_SHADOW = '0 2px 8px rgba(0, 0, 0, 0.08)';
const TITLE_LINK_ATTRIBUTE = 'data-navigation-card-link';

const NavigationCardRoot = styled(Card)(({ theme }) => ({
  position: 'relative',
  transition: theme.transitions.create(['box-shadow', 'border-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      boxShadow: HOVER_SHADOW,
    },
    [`&:hover [${TITLE_LINK_ATTRIBUTE}]`]: {
      color: theme.palette.primary.main,
    },
  },
  [`&:has([${TITLE_LINK_ATTRIBUTE}]:focus-visible)`]: {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  paddingBottom: theme.spacing(1),
}));

const TitleLink = styled('a')(({ theme }) => ({
  ...theme.typography.h4,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.text.primary,
  textDecoration: 'none',
  overflowWrap: 'anywhere',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
  },
  '&:focus-visible': {
    outline: 'none',
  },
}));

const HeaderText = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
}));

const Action = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
  marginTop: theme.spacing(-0.5),
  marginRight: theme.spacing(-1),
}));

const Content = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  pointerEvents: 'none',
  paddingInline: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  '& a, & button, & input, & [role="button"]': {
    pointerEvents: 'auto',
  },
}));

export function NavigationCard(props: Props) {
  return (
    <NavigationCardRoot data-testid={props['data-testid'] ?? 'navigation-card'}>
      <Header>
        <HeaderText>
          <TitleLink href={props.href} onClick={props.onClick} {...{ [TITLE_LINK_ATTRIBUTE]: '' }}>
            {props.title}
          </TitleLink>
          {props.tags ? (
            <Stack direction="row" spacing={0.5} wrap="wrap">
              {props.tags}
            </Stack>
          ) : null}
        </HeaderText>
        {props.action ? <Action>{props.action}</Action> : null}
      </Header>
      {props.children ? (
        <Content>
          <Stack spacing={1}>
            <Divider />
            {props.children}
          </Stack>
        </Content>
      ) : null}
    </NavigationCardRoot>
  );
}

export type { Props as NavigationCardProps };
